import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import { AccountingExportOption } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useMainBankAccountId from 'common/hooks/useMainBankAccountId';
import { FCWithChildren } from 'common/types';

import { TimeFrameType } from '../types';
import { UserProfile } from './useGetUserProfile';

// Flow config
interface ExportCreationFlowConfig {
  isRecurring: boolean;
  user: UserProfile;
}

// Shared state
interface ExportCreationSharedState {
  name: string;
  email: string;
  endDate: string;
  startDate: string;
  timeframeType: TimeFrameType | '';
  selectedContentIds: AccountingExportOption[];
  bankAccountId: string;
  companyProfileId: string;
}

const INITIAL_SHARED_STATE: ExportCreationSharedState = {
  bankAccountId: '',
  companyProfileId: '',
  email: '',
  endDate: '',
  name: '',
  selectedContentIds: [],
  startDate: '',
  timeframeType: '',
};

// Context
interface ExportCreationContext {
  flowConfig: ExportCreationFlowConfig;
  sharedState: ExportCreationSharedState;
  setSharedState: Dispatch<SetStateAction<ExportCreationSharedState>>;
}

const EXPORT_CREATION_CONTEXT = createContext<
  ExportCreationContext | undefined
>(undefined);

// Provider
interface ExportCreationContextProviderProps {
  flowConfig: ExportCreationFlowConfig;
}

export const ExportCreationContextProvider: FCWithChildren<
  ExportCreationContextProviderProps
> = ({ children, flowConfig }) => {
  const { mainBankAccountId = '' } = useMainBankAccountId(); // should always be defined in this context
  const { companyProfileId = '' } = useCompanyContext(); // should always be defined in this context

  const [sharedState, setSharedState] = useState(INITIAL_SHARED_STATE);

  useEffect(() => {
    setSharedState((prevState) => ({
      ...prevState,
      bankAccountId: mainBankAccountId,
      companyProfileId,
    }));
  }, [mainBankAccountId, companyProfileId]);

  return (
    <EXPORT_CREATION_CONTEXT.Provider
      value={{ flowConfig, setSharedState, sharedState }}
    >
      {children}
    </EXPORT_CREATION_CONTEXT.Provider>
  );
};

// Hook
export const useExportCreationContext = () => {
  const context = useContext(EXPORT_CREATION_CONTEXT);

  if (!context) {
    throw new Error(
      '"useExportCreationContext" can only be used under ExportCreationContextProvider',
    );
  }

  return context;
};
